import { API_ROW_CEILING } from "./config";
import { csvFilename, downloadCsv, toCsv } from "./csv";

/* Take away what is on screen, not what fits on the page.
 *
 * The Download CSV button used to write out the fifty rows the table happened
 * to be showing, on a screen that said "6,760 matches" directly above it. That
 * is the failure this application can least afford: a control that says one
 * thing and does a smaller one, quietly.
 *
 * So a download re-asks for the whole narrowed set. PostgREST stops at
 * API_ROW_CEILING rows for any one request, and that ceiling is deliberate, so
 * a set bigger than it cannot be handed over this way. When that happens the
 * person gets the rows that did come back and a sentence saying what is
 * missing and what to do instead, rather than a short file they have no reason
 * to distrust.
 *
 * Returns null when everything asked for was delivered, and a sentence when it
 * was not. */
export async function downloadNarrowed<T>({
  name, columns, total, fetchAll,
}: {
  name: string;
  columns: string[];
  /** What the screen says it has, or null when the count has not landed. */
  total: number | null;
  fetchAll: (ceiling: number) => Promise<T[]>;
}): Promise<string | null> {
  const rows = await fetchAll(API_ROW_CEILING);
  downloadCsv(csvFilename(name), toCsv(columns, rows as unknown as Record<string, unknown>[]));

  const missing = total !== null && total > rows.length;
  const hitCeiling = rows.length >= API_ROW_CEILING;
  if (!missing && !hitCeiling) return null;

  const got = rows.length.toLocaleString();
  return total !== null
    ? `That file has the first ${got} of ${total.toLocaleString()} matches. `
      + `One download stops at ${API_ROW_CEILING.toLocaleString()} rows. `
      + `Narrow the list further, or build it as a campaign export, which has no ceiling.`
    : `That file has ${got} rows, the most one download allows. `
      + `There may be more. Narrow the list, or build it as a campaign export.`;
}
